// Session-safe post-login redirect helper.
//
// Why sessionStorage (not react-router location.state)?
//   Google OAuth bounces the user OUT of our origin to accounts.google.com and
//   back to /auth/callback — location.state does not survive that round-trip.
//   sessionStorage is per-tab and per-origin, so it persists across the OAuth
//   redirect chain and is cleared automatically when the tab closes.
//
// Use:
//   import { saveReturnUrl, savePendingIntent, consumeReturnUrl, consumePendingIntent }
//     from "@/lib/postLoginRedirect";
//
//   // Before sending an unauthenticated user to /login:
//   saveReturnUrl(window.location.pathname + window.location.search);
//   savePendingIntent({ type: "enroll", course_id, payment_plan, coupon_code });
//   navigate("/login");
//
//   // After login (Login.jsx / Register.jsx / AuthCallback.jsx):
//   const dest = consumeReturnUrl() || defaultDashboardFor(user.role);
//   navigate(dest, { replace: true });
//
//   // On the destination page (e.g. CourseDetail), once user is loaded:
//   const intent = consumePendingIntent();
//   if (intent?.type === "enroll") triggerEnroll(intent);

const RETURN_URL_KEY = "cwk_return_url";
const PENDING_INTENT_KEY = "cwk_pending_intent";

// Only persist app-internal paths — never absolute URLs to other origins.
const isSafeInternalPath = (path) =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

export function saveReturnUrl(path) {
  if (!isSafeInternalPath(path)) return;
  // Never bounce back to auth pages
  if (/^\/(login|register|auth\/callback)(\b|$)/.test(path)) return;
  try { sessionStorage.setItem(RETURN_URL_KEY, path); } catch { /* storage disabled */ }
}

export function consumeReturnUrl() {
  try {
    const v = sessionStorage.getItem(RETURN_URL_KEY);
    if (v) sessionStorage.removeItem(RETURN_URL_KEY);
    return isSafeInternalPath(v) ? v : null;
  } catch { return null; }
}

export function peekReturnUrl() {
  try {
    const v = sessionStorage.getItem(RETURN_URL_KEY);
    return isSafeInternalPath(v) ? v : null;
  } catch { return null; }
}

export function savePendingIntent(intent) {
  if (!intent || typeof intent !== "object" || !intent.type) return;
  try { sessionStorage.setItem(PENDING_INTENT_KEY, JSON.stringify(intent)); }
  catch { /* storage disabled */ }
}

export function consumePendingIntent() {
  try {
    const raw = sessionStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_INTENT_KEY);
    return JSON.parse(raw);
  } catch { return null; }
}

export function clearPendingIntent() {
  try { sessionStorage.removeItem(PENDING_INTENT_KEY); } catch { /* noop */ }
}

export function defaultDashboardFor(role) {
  return role === "admin"
    ? "/dashboard/admin"
    : role === "teacher"
      ? "/dashboard/teacher"
      : "/dashboard/student";
}
